import { useCallback, useEffect, useState } from "react";
import api, { downloadFile } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useStationery } from "./useStationery";

export const EMPTY_LETTER = { to_contact_id: "", opening: "", signer_title: "", architect: "", bid_date: "", base_bid_amount: "", delivery_terms: "", tax_note: "", proposal_number: "", validity_days: "" };
const EMPTY_COMPANY = { address: "", phone: "", license_number: "", email: "", website: "" };
const fail = (fallback) => (e) => toast.error(e?.response?.data?.detail || fallback);
const runParams = (runId) => (runId ? { run_id: runId } : {});

const settingsPayload = (form, includeScope, alts) => ({
  ...form,
  validity_days: form.validity_days === "" || form.validity_days === null ? null : Number(form.validity_days),
  to_contact_id: form.to_contact_id || null,
  include_scope: includeScope,
  alternates: alts.filter((a) => a.description.trim()).map(({ description, kind, amount }) => ({ description, kind, amount })),
});

// Bid clarification letter: settings, company profile, save/download (stationery lives in useStationery).
export function useBidLetter({ projectId, projectName, runId, open }) {
  const { user, activeWorkspaceId } = useAuth();
  const [L, setL] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [company, setCompany] = useState(EMPTY_COMPANY);
  const [form, setForm] = useState(EMPTY_LETTER);
  const [includeScope, setIncludeScope] = useState(true);
  const [alts, setAlts] = useState([]);
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    const [l, c] = await Promise.all([api.get(`/projects/${projectId}/letter`, { params: { format: "json", ...runParams(runId) } }), api.get(`/projects/${projectId}/contacts`)]);
    setL(l.data); setContacts(c.data);
    setIncludeScope(l.data.include_scope !== false);
    setAlts((l.data.alternates || []).map(({ description, kind, amount }, i) => ({ key: `alt-${i}`, description, kind, amount: amount || "" })));
    setForm(Object.fromEntries(Object.keys(EMPTY_LETTER).map((k) => [k, l.data.settings?.[k] ?? ""])));
    setCompany({ ...EMPTY_COMPANY, ...(l.data.company || {}) });
  }, [projectId, runId]);
  useEffect(() => { if (open) load(); }, [open, load]);

  const stationery = useStationery({ L, user, activeWorkspaceId, reload: load });

  const saveCompany = async () => { try { await api.put(`/workspaces/${activeWorkspaceId}/profile`, company); toast.success("Company profile saved"); await load(); } catch (e) { fail("Save failed")(e); } };
  const save = async () => {
    setBusy("save");
    try { await api.put(`/projects/${projectId}/letter/settings`, settingsPayload(form, includeScope, alts)); toast.success("Letter settings saved"); await load(); }
    catch (e) { fail("Save failed")(e); } finally { setBusy(""); }
  };
  const download = async (fmt) => {
    setBusy(fmt);
    try { const q = new URLSearchParams({ format: fmt, ...runParams(runId) }); await downloadFile(`/projects/${projectId}/letter?${q}`, `bid-clarifications-${projectName}.${fmt}`); toast.success(`Letter ${fmt.toUpperCase()} downloaded`); }
    catch (e) { toast.error(e.message); } finally { setBusy(""); }
  };
  return { L, contacts, company, setCompany, form, setForm, includeScope, setIncludeScope, alts, setAlts, busy, ...stationery, saveCompany, save, download };
}
